import Colors from "@cosine/lib/Colors";
import { BoxGeometry, type ColorRepresentation, EdgesGeometry, Material, Mesh, MeshBasicMaterial, Object3D, Vector3 } from "three";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial";
import { LineSegments2 } from "three/examples/jsm/lines/LineSegments2.js";
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry";

export interface IsoBlockOptions {
  color: ColorRepresentation,
  material?: Material,
  position?: Vector3,
  height?: number,
}

export type IsoBlockAlign = "start" | "end";

export default class IsoBlock {
  geometry: BoxGeometry = new BoxGeometry(1, 1, 1);
  edgeGeometry: EdgesGeometry;
  edgeLineGeometry: LineSegmentsGeometry;
  edgeLineMaterial: LineMaterial;
  edges: LineSegments2;
  material: Material;
  mesh: Mesh;
  private _x = 0;
  private _y = 0;
  private _z = 0;
  private _xAlign: IsoBlockAlign = "start";
  private _zAlign: IsoBlockAlign = "start";

  constructor (parent: Object3D, options: IsoBlockOptions) {
    const color = options.color;

    this.material = options.material || new MeshBasicMaterial({
      color,
      polygonOffset: true,
      polygonOffsetFactor: 1,
      polygonOffsetUnits: 1,
    });
    this.mesh = new Mesh(this.geometry, this.material);
    this.mesh.castShadow = false;

    this.edgeGeometry = new EdgesGeometry(this.geometry);
    this.edgeLineMaterial = new LineMaterial({
      color: Colors.black,
      linewidth: 1,
    });

    this.edgeLineGeometry = new LineSegmentsGeometry().setPositions(Array.from(this.edgeGeometry.attributes.position.array));
    this.edges = new LineSegments2(this.edgeLineGeometry, this.edgeLineMaterial);
    this.mesh.add(this.edges);

    this._x = options.position?.x || 0;
    this._y = options.position?.y || 0;
    this._z = options.position?.z || 0;

    if (options.height) {
      this.height = options.height;
    } else {
      this.updatePosition();
    }

    parent.add(this.mesh);
  }

  get visible (): boolean {
    return this.mesh.visible;
  }

  set visible (visible: boolean) {
    this.mesh.visible = visible;
  }

  get x (): number {
    return this._x;
  }

  set x (x: number) {
    this._x = x;
    this.updatePosition();
  }

  get y (): number {
    return this._y;
  }

  set y (y: number) {
    this._y = y;
    this.updatePosition();
  }

  get z (): number {
    return this._z;
  }

  set z (z: number) {
    this._z = z;
    this.updatePosition();
  }

  get width (): number {
    return this.mesh.scale.x;
  }

  set width (width: number) {
    this.mesh.scale.x = width;
    this.updatePosition();
  }

  get height (): number {
    return this.mesh.scale.y;
  }

  set height (height: number) {
    this.mesh.scale.y = height;
    this.updatePosition();
  }

  get depth (): number {
    return this.mesh.scale.z;
  }

  set depth (depth: number) {
    this.mesh.scale.z = depth;
    this.updatePosition();
  }

  get xAlign (): IsoBlockAlign {
    return this._xAlign;
  }

  set xAlign (xAlign: IsoBlockAlign) {
    this._xAlign = xAlign;
    this.updatePosition();
  }

  get zAlign (): IsoBlockAlign {
    return this._zAlign;
  }

  set zAlign (zAlign: IsoBlockAlign) {
    this._zAlign = zAlign;
    this.updatePosition();
  }

  private updatePosition () {
    this.mesh.position.x = this._x + (this._xAlign === "start" ? 1 : -1) * (this.mesh.scale.x - 1) / 2;
    this.mesh.position.y = this.mesh.scale.y === 0 ? -0.1 : this._y + this.mesh.scale.y / 2;
    this.mesh.position.z = this._z + (this._zAlign === "start" ? 1 : -1) * (this.mesh.scale.z - 1) / 2;
    this.mesh.castShadow = false; // this.mesh.scale.y !== 0;
  }

  dispose () {
    this.mesh.removeFromParent();
    this.geometry.dispose();
    this.material.dispose();
    this.edgeGeometry.dispose();
    this.edgeLineGeometry.dispose();
    this.edgeLineMaterial.dispose();
  }
}
